export interface VisitorSession {
  userId: string;
  firstSeenDate: string;
  loginCount: number;
  lastLoginDate: string;
  username: string;
}

export interface VisitorIdentificationResult {
  visitorType: "new_user" | "returning_user";
  session: VisitorSession;
  isFirstLogin: boolean;
}

const VISITOR_COOKIE_KEY = "sootchy_visitor_sessions";
const COOKIE_EXPIRY_DAYS = 365;

function setCookie(name: string, value: string, days: number): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

function getCookie(name: string): string | null {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

function getVisitorSessions(): Record<string, VisitorSession> {
  try {
    const stored = getCookie(VISITOR_COOKIE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function saveVisitorSessions(sessions: Record<string, VisitorSession>): void {
  try {
    const jsonString = JSON.stringify(sessions);
    setCookie(VISITOR_COOKIE_KEY, jsonString, COOKIE_EXPIRY_DAYS);
  } catch {
    // Handle error if needed, e.g. log to an error tracking service
  }
}

export function identifyVisitor(user: {
  id: string;
  username: string;
}): VisitorIdentificationResult {
  if (!user.id || !user.username) {
    throw new Error(
      "User ID and username are required for visitor identification",
    );
  }

  const sessions = getVisitorSessions();
  const userId = user.id;
  const currentDate = new Date().toISOString();

  let session = sessions[userId];
  let isFirstLogin = false;

  if (!session) {
    session = {
      userId,
      firstSeenDate: currentDate,
      loginCount: 1,
      lastLoginDate: currentDate,
      username: user.username,
    };
    isFirstLogin = true;
  } else {
    session.loginCount += 1;
    session.lastLoginDate = currentDate;
    session.username = user.username;
  }

  sessions[userId] = session;
  saveVisitorSessions(sessions);

  const result: VisitorIdentificationResult = {
    visitorType: isFirstLogin ? "new_user" : "returning_user",
    session,
    isFirstLogin,
  };

  return result;
}

export function getVisitorSession(userId: string): VisitorSession | null {
  const sessions = getVisitorSessions();
  return sessions[userId] || null;
}

export function clearVisitorData(): void {
  setCookie(VISITOR_COOKIE_KEY, "", -1);
}
